"use client";

import { useI18n } from "@/lib/i18n";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { signIn, signOut, useSession } from "next-auth/react";

export function UserMenu() {
  const { t } = useI18n();
  const { data: session, status } = useSession();
  const isAuthed = status === "authenticated";

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const displayName =
    session?.user?.name || session?.user?.email || "User";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-neutral-800 hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-600"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="text-sm">
          {isAuthed ? displayName : t("user.anonymous")}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg bg-neutral-900 border border-neutral-700 shadow-lg z-50">
          <ul className="text-sm py-1" role="menu" aria-label={t("user.menu")}>
            {!isAuthed ? (
              <li role="none">
                <button
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    signIn("authentik", { callbackUrl: "/" });
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-neutral-800 transition"
                >
                  {t("user.login")}
                </button>
              </li>
            ) : (
              <>
                <li role="none">
                  <Link
                    role="menuitem"
                    href="/settings"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 font-medium hover:bg-neutral-800 transition"
                  >
                    {t("user.settings")}
                  </Link>
                </li>
                <li role="none">
                  <Link
                    role="menuitem"
                    href="/settings/profile"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 hover:bg-neutral-800 transition"
                  >
                    {t("user.profile")}
                  </Link>
                </li>
                <li role="none">
                  <Link
                    role="menuitem"
                    href="/settings/appearance"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 hover:bg-neutral-800 transition"
                  >
                    {t("user.appearance")}
                  </Link>
                </li>

                <li role="none">
                  <Link
                    role="menuitem"
                    href="/settings/notifications"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 hover:bg-neutral-800 transition"
                  >
                    {t("user.notifications")}
                  </Link>
                </li>

                <li role="none">
                  <Link
                    role="menuitem"
                    href="/settings/cameras"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 hover:bg-neutral-800 transition"
                  >
                    {t("user.cameras")}
                  </Link>
                </li>

                <li className="border-t border-neutral-700 mt-1 pt-1" role="none">
                  <button
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      signOut({ callbackUrl: "/login" });
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-neutral-800 text-rose-400 transition"
                  >
                    {t("user.logout")}
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
